// Pure (RN-freie) Logik für die Ableitung des restrictionStatus im Web-
// Planungsflow (Restrictions -> Details-Schritt). Spiegelt das Verhalten des
// React-Native-Flows (client/lib/manualRestrictionFlow.ts: deriveDetailsRestrictionStatus)
// für die Web-Seite wider, damit Drift zwischen Mobile- und Web-Logik durch
// tsx-Tests auffällt (siehe web/src/lib/__tests__/restrictionDerivation.test.ts).
//
// Im Web-Modell wird die manuelle Antwort des Piloten als
// (hasRestrictions, restrictionStatus) erfasst:
//   - Karten-Schritt (Land ohne eigene Daten): hasRestrictions ja/nein.
//   - Restrictions-Schritt (Land mit eigenen Daten): Checkbox + Status-Radio.
// Die § 21h-Abdeckungsbewertung (assessAvCoverage) liefert einen Vorschlag, der
// die manuelle Antwort überschreibt, wenn Geozonen ausgewählt wurden.

import type { AvCoverageResult } from "../allgemeinerlaubnis";
import type { RestrictionStatus } from "./types";

export interface WebRestrictionStatusInput {
  // Ob am Standort Einschränkungen bestehen (manuelle Antwort des Piloten).
  hasRestrictions: boolean;
  // Aktuell gewählter/vorbelegter Status ("none" = keine Genehmigung / Flug nur
  // mit Auflagen nicht freigegeben, analog zum Mobile-"nofly").
  manualStatus: RestrictionStatus;
  // Ergebnis der § 21h-Abdeckungsbewertung (assessAvCoverage). suggestedStatus
  // ist null, wenn keine Bewertung greift (nicht DE / keine Geozonen gewählt).
  coverage: AvCoverageResult;
}

// Der von der AV-Bewertung abgeleitete Vorschlag, der den manuellen Status
// überschreibt — oder null, wenn keine Bewertung greift (dann bleibt die
// manuelle Antwort bestehen). Entspricht der bisherigen Inline-Logik im
// PlanningWizard.
export function deriveSuggestedRestrictionStatus(
  hasRestrictions: boolean,
  coverage: AvCoverageResult,
): "approved" | "limited" | null {
  if (!hasRestrictions) return null;
  const s = coverage.suggestedStatus;
  return s === "approved" || s === "limited" ? s : null;
}

// Effektiver restrictionStatus, der an den Details-/Planungs-Eintrag
// weitergereicht wird.
//   - keine Einschränkungen            -> "none" (kein positiver Status)
//   - AV-Vorschlag vorhanden           -> Vorschlag ("approved" | "limited")
//   - sonst manuelle Antwort           -> unverändert übernommen
//     (insb. "none" als "nofly"-Äquivalent -> bleibt "none")
export function deriveWebRestrictionStatus(
  input: WebRestrictionStatusInput,
): RestrictionStatus {
  if (!input.hasRestrictions) return "none";
  const suggested = deriveSuggestedRestrictionStatus(true, input.coverage);
  return suggested ?? input.manualStatus;
}
